/*
  eslint-disable no-console --
  * Required for logging errors
*/
import { type Result } from "@userorbit/types/error-handlers";

let isInitializing = false;
let isInitialized = false;

// Load the SDK, return the result
const loadUserorbitSDK = async (cdnHostParam: string): Promise<Result<void>> => {
  if (!window.userorbit) {
    const scriptTag = document.createElement("script");
    scriptTag.type = "text/javascript";
    scriptTag.src = `${cdnHostParam}/userorbit.umd.cjs`;
    scriptTag.async = true;

    const getUserorbit = async (): Promise<void> =>
      new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error(`Userorbit SDK loading timed out`));
        }, 10000);
        scriptTag.onload = () => {
          clearTimeout(timeoutId);
          resolve();
        };
        scriptTag.onerror = () => {
          clearTimeout(timeoutId);
          reject(new Error(`Failed to load Userorbit SDK`));
        };
      });

    document.head.appendChild(scriptTag);

    try {
      await getUserorbit();
      return { ok: true, data: undefined };
    } catch (error) {
      const err = error as { message?: string };

      return {
        ok: false,
        error: new Error(err.message ?? `Failed to load Userorbit SDK`),
      };
    }
  }

  return { ok: true, data: undefined };
};

const functionsToProcess: { prop: string; args: unknown[] }[] = [];

export const loadUserorbitToProxy = async (prop: string, ...args: unknown[]): Promise<void> => {
  // all of this should happen when not initialized:
  if (!isInitialized) {
    if (prop === "init") {
      if (isInitializing) {
        console.warn("🧱 Userorbit - Warning: Userorbit is already initializing.");
        return;
      }

      // reset the initialization state
      isInitializing = true;

      const cdnHost = (args[0] as { cdnHost: string }).cdnHost || "https://cdn.userorbit.com";
      const loadSDKResult = await loadUserorbitSDK(cdnHost);

      if (loadSDKResult.ok) {
        if (window.userorbit) {
          const initFn = window.userorbit.init as (...args: unknown[]) => unknown;
          void initFn(...args);

          isInitializing = false;
          isInitialized = true;

          // process the queued functions
          for (const { prop: functionProp, args: functionArgs } of functionsToProcess) {
            type UserorbitProp = keyof typeof window.userorbit;

            if (typeof window.userorbit[functionProp as UserorbitProp] !== "function") {
              console.error(`🧱 Userorbit - Error: Method ${functionProp} does not exist on userorbit`);
              continue;
            }
            const fn = window.userorbit[functionProp as UserorbitProp] as (...args: unknown[]) => unknown;
            fn(...functionArgs);
          }
        }
      }
    } else {
      console.warn(
        "🧱 Userorbit - Warning: Userorbit not initialized. This method will be queued and executed after initialization.",
      );

      functionsToProcess.push({ prop, args });
    }
  } else if (window.userorbit) {
    type Userorbit = typeof window.userorbit;
    type FunctionProp = keyof Userorbit;
    const functionPropTyped = prop as FunctionProp;
    const fn = window.userorbit[functionPropTyped] as (...args: unknown[]) => unknown;

    await fn(...args);
  }
};
